import type { Faq as FaqType } from 'libs/http/api/content/content.types';

type Translate = (key: string) => string;

export const getFaqData = (t: Translate): FaqType[] => [
  {
    id: 'where-does-the-data-come-from',
    default_opened: true,
    question: t('Where does the data actually come from?'),
    answer: t(
      'More than 400 official registries across 200+ countries and territories, taken from the government source rather than from an intermediary. Every field on a response carries the register it came from and the date it was last synced, which is what lets a verification result stand as evidence rather than as a claim.',
    ),
  },
  {
    id: 'how-current-is-a-registry-record',
    question: t('How current is a registry record?'),
    answer: t(
      'Registry data is daily-synced. The API responds instantly, but what it returns is the register as of its last sync, and that sync date comes back on every response. You can always show an auditor exactly what you decided on and when.',
    ),
  },
  {
    id: 'what-happens-when-a-seller-cannot-be-resolved',
    question: t('What happens when a seller cannot be resolved?'),
    answer: t(
      'You get the candidates and a confidence score rather than a silent guess. Below your threshold the response says so and the seller routes to review instead of being auto-approved on a weak match. Silent low-confidence matching is how bad records enter a system and never leave it.',
    ),
  },
  {
    id: 'seller-address-does-not-match-the-register',
    question: t("A seller's address does not match the register. Now what?"),
    answer: t(
      'You get both values, the source, and a mismatch flag. Trading addresses legitimately differ from registered addresses in most jurisdictions, so a mismatch is not a rejection. The policy call stays with you and the evidence for it arrives with the response.',
    ),
  },
  {
    id: 'do-you-cover-sole-traders-and-individual-sellers',
    question: t('Do you cover sole traders and individual sellers?'),
    answer: t(
      'Registry verification applies to entities that appear in a company or trade register. Sole traders sit outside that in many jurisdictions, and where a register holds nothing we return that rather than a low-confidence guess. Coverage by country and entity type is shared before you commit, so scope is a known number instead of a pilot discovery.',
    ),
  },
  {
    id: 'does-this-satisfy-dsa-article-30',
    question: t('Does this satisfy DSA Article 30 on its own?'),
    answer: t(
      'It covers the part of Article 30 that is hardest to build: the trade register and registration number obtained before the trader lists, checked against an official source, with an audit trail behind it. It does not cover your remaining obligations such as the self-certification, the payment account details or the suspension workflow, and nothing here is legal advice. Your counsel signs off on compliance; we supply the evidence layer underneath it.',
    ),
  },
  {
    id: 'can-we-show-verified-seller-details-to-buyers',
    question: t('Can we show verified seller details to our buyers?'),
    answer: t(
      'Yes. Licensing includes redistribution rights, which matters because the INFORM Act requires marketplaces to disclose certain seller identity information to consumers, and the GPSR requires an identifiable responsible economic operator on the offer itself.',
    ),
  },
  {
    id: 'what-about-sellers-already-onboarded',
    question: t('What do we do about sellers we have already onboarded?'),
    answer: t(
      'Run them as a batch through the same check. Dissolved entities, mismatched identifiers and sellers approved under an earlier standard tend to surface in the first pass. Send a sample and we return the registry position on each one, with sources, before you buy anything.',
    ),
  },
  {
    id: 'how-is-this-different-from-dandb-moodys-or-kyb',
    question: t("How is this different from D&B, Moody's or a KYB point solution?"),
    answer: t(
      'Sourcing is traced to the government register rather than assembled through intermediaries. Provenance sits at field level rather than record level, so you can defend an individual decision and not just the vendor relationship. And the identifiers are open, so the record joins to your own systems without a proprietary key you keep paying to keep using.',
    ),
  },
  {
    id: 'how-is-it-priced',
    question: t('How is it priced?'),
    answer: t(
      'By volume and by the surfaces you use, on an annual contract. Verification calls, the change feed and batch backfill are priced separately because platforms use them at very different ratios. We quote against your actual seller volume and jurisdiction mix rather than a list price built for a business shaped differently from yours.',
    ),
  },
];
